import {
  DEFAULT_ROWS_PER_PAGE,
  ExpandableTable,
  LazyTableRow,
  RoundedBox,
  SimpleSelect,
  TableCellType,
  TableSortType,
} from "@knowfully/ui.common";
import { useIntl } from "react-intl";
import AgreementExpandedTable from "components/AgreementExpandedTable";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  CourseStatus,
  CourseDocumentFilter,
  CourseDocumentModel,
  ListRequestOfCourseDocumentFilter,
} from "services/facultyPortalClient";
import CourseDocumentsFilter from "./CourseDocumentsFilter";
import SkuUserAgreementStatusChip from "./SkuUserAgreementStatusChip";
import { Box, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";

const styles = {
  courseTitle: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  courseLink: {
    textDecoration: "none",
    color: "primary.dark",
    fontWeight: "bold",
  },
};

export default function DocumentsPage() {
  const { formatMessage: t } = useIntl();
  const { documentService, courseDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("eventStartDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filter, setFilter] = useState({} as CourseDocumentFilter);
  const [courseStatusFilter, setCourseStatusFilter] = useState(
    CourseStatus.Active
  );

  const { data, isFetching } = useQuery(
    [
      "getCourseDocuments",
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
      courseStatusFilter,
    ],
    () =>
      documentService.getCourseDocuments(
        courseStatusFilter,
        ListRequestOfCourseDocumentFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: filter,
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter, courseStatusFilter]);

  const SkuUserAgreementStatus = ({
    value,
  }: {
    value: CourseDocumentModel;
  }) => {
    return (
      <SkuUserAgreementStatusChip
        skuUserAgreementInfo={value.skuUserAgreementInfo}
      />
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "title",
      type: "node",
      label: t({ id: "courseTitle" }),
      View: ({ value }: { value: CourseDocumentModel }) => (
        <Box sx={styles.courseTitle}>
          <MuiLink
            component={Link}
            to={courseDetailsUrl(value.id)}
            sx={styles.courseLink}
          >
            {value.title}
          </MuiLink>
        </Box>
      ),
    },
    {
      accessor: "format",
      type: "text",
      label: t({ id: "format" }),
    },
    {
      accessor: "eventStartDate",
      type: "date",
      label: t({ id: "courseDate" }),
    },
    {
      accessor: "type",
      type: "text",
      noSort: true,
      label: t({ id: "facultyRole" }),
    },
    {
      accessor: "value",
      type: "node",
      noSort: true,
      label: t({ id: "status" }),
      View: SkuUserAgreementStatus,
    },
  ];

  const documents = (data?.data ?? []).map(
    (document) =>
      ({
        ...document,
        isExpandable: !!document.skuUserAgreementInfo,
        renderExpandedData: () =>
          document.skuUserAgreementInfo ? (
            <AgreementExpandedTable
              key={document.id}
              skuUserAgreement={document.skuUserAgreementInfo}
            />
          ) : null,
      }) as LazyTableRow
  );

  const statusOptions = [
    { value: CourseStatus.Active, id: CourseStatus.Active },
    { value: CourseStatus.Inactive, id: CourseStatus.Inactive },
  ];

  const StatusSelect = (
    <SimpleSelect
      sx={{ width: 130 }}
      options={statusOptions}
      onChange={(v) => {
        setCourseStatusFilter(v as CourseStatus);
        setFilter(
          (prev) =>
            ({
              ...prev,
              resourceTypeIds: [] as string[],
            }) as CourseDocumentFilter
        );
      }}
      label={t({ id: "courseStatus" })}
      value={courseStatusFilter}
    />
  );

  return (
    <>
      <RoundedBox>
        <CourseDocumentsFilter
          onChange={setFilter}
          courseStatus={courseStatusFilter}
        />
      </RoundedBox>
      <RoundedBox>
        <ExpandableTable
          aria-label={t({ id: "myDocuments" })}
          collapsable
          rowCount={data?.totalRecords ?? 0}
          rows={documents}
          cells={cells}
          page={page}
          setPage={setPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          loading={isFetching}
          titleComponent={StatusSelect}
        />
      </RoundedBox>
    </>
  );
}
